import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  OwnerSetupCheckEmailRequest,
  OwnerSetupConsumeRequest,
  OwnerSetupProfileRequest,
} from '@naya/shared'
import { ownerApi } from '../ownerApi.js'
import { ownerMeKey } from './useOwnerSession.js'
import type { SetupConsumeResponse, SetupValidateResponse } from '../types.js'

/**
 * Validate a setup token — GET /api/owner/setup/validate?token=. Always
 * resolves (the API returns `{ valid:false, reason }` rather than an error for
 * a bad token), so the wizard renders an "expired link" page on
 * `valid === false` instead of an error boundary.
 */
export function useSetupValidate(token: string | undefined) {
  return useQuery<SetupValidateResponse>({
    queryKey: ['owner', 'setup', 'validate', token],
    enabled: !!token,
    retry: false,
    staleTime: Infinity, // token validity doesn't change under us mid-session
    queryFn: () =>
      ownerApi.get<SetupValidateResponse>(
        `/owner/setup/validate?token=${encodeURIComponent(token!)}`,
      ),
  })
}

/**
 * Step-1 email availability check (POST /owner/setup/check-email). Token-gated;
 * returns `{ available }` so the wizard can flag a taken email immediately
 * rather than at the password/consume step.
 */
export function useSetupCheckEmail() {
  return useMutation({
    mutationFn: (input: OwnerSetupCheckEmailRequest) =>
      ownerApi.post<{ available: boolean }>('/owner/setup/check-email', input),
  })
}

/**
 * Consume a setup token — sets the password, marks the token used, opens the
 * owner session. On success the owner session query is invalidated so the
 * shell picks up the now-authenticated state.
 */
export function useSetupConsume() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: OwnerSetupConsumeRequest) =>
      ownerApi.post<SetupConsumeResponse>('/owner/setup/consume', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ownerMeKey })
    },
  })
}

// Slice 6 — wizard step 2: owner's real email + display_name. Backend
// inserted a placeholder owner row at admin-create.
export function useSetupProfile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: OwnerSetupProfileRequest) =>
      ownerApi.patch<{ user: unknown }>('/owner/setup/profile', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ownerMeKey })
    },
  })
}

